import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import useAutoRunOnFilterChange from '@hooks/useAutoRunOnFilterChange'
import useLatestRun from '@hooks/useLatestRun'
import dayjs from 'dayjs'
import accountingReportService, { accountingEndpoint, accountingAttachFormats } from '@api/v1/accountingReportService'
import reportJobService from '@api/v1/reportJobService'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { dateRangeField, choiceField } from '@components/reports/specializedFilterFields'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import ReportGroupedGrid from '@components/reports/ReportGroupedGrid'
import CategorySelect from '@components/reports/CategorySelect'
import { describeFilters, dateRangePart, basisPart } from '@components/reports/reportFilterSummary'
import { pnlSavedCriteria } from '@components/reports/savedRunCriteria'
import { useReportExportView } from '@components/reports/reportExportViewContext'
import useGlCategories, { allCategoryIds, toFilteredCategories, categoryNames } from '@hooks/useGlCategories'
import { buildSegmentMatrix, hasPivotColumns } from './segmentMatrix'
import { buildPlStatementReport, netIncomeOf, ROLLUP_UNAVAILABLE_REASONS } from './plStatement'
import {
  priorPeriodWire,
  compareRangeRefusal,
  COMPARE_RANGE_MESSAGES,
  comparisonWindowOf,
  describeComparisonWindow,
} from './plCompareWindow'

// GUARDRAIL: P&L ordering is semantic (Income before COGS before Expenses …).
// Sections map 1:1 to grid groups IN SERVER ORDER — no flattening, no
// cross-section sorting. Totals pass through from the server payload.
//
// Comparison periods (CC-49746): CB's GetPLReport JSON contract was confirmed
// live on vladdemo (2026-07-16) — with pLComparePeriods set, every lineItem
// carries `comparisonAmount` + `comparisonAmountDiff`, every section carries
// `comparisonAmountSectionTotal(+Diff)`, and the report carries
// `netComparisonIncome` / `netIncomeDiff`. Line and section values pass through
// from the server; the % of change is derived per row from the two server
// figures, and the report-level change is the plain difference of CB's two Net
// Income figures, never `netIncomeDiff` (spec 127 §4.3 (4) — see plStatement.js,
// where the statement is built).

// CC-49746: these keys are CB's own `PLComparePeriods` enum member names, and the
// values below are the integers V1 posts. Getting this wrong is silent: MVC's
// DefaultModelBinder cannot convert an unrecognized string to the enum, so it
// yields null, and because the action parameter has a default the controller
// receives `PLComparePeriods.None`. CB then answers **HTTP 200 with every
// ComparisonAmount at 0** — no error anywhere to notice. Downstream that renders
// as a blank Prior column (fmtMoney maps 0 to ''), a Change identical to Amount
// (Amount - 0), and '—' for every percentage. cb2 previously sent
// 'PriorYear'/'PriorPeriod'/'PriorMonth', none of which are enum members, so the
// comparison was never actually requested. 'PriorMonth' had no server equivalent
// at all; V1's third option is Year-To-Date.
const PL_COMPARE_WIRE = { None: 0, PreviousPeriod: 1, PreviousYear: 2, YearToDate: 3 }
// Reverse of PL_COMPARE_WIRE — recover the compare-period NAME from a saved job's
// wire integer so a reopened comparison renders its compare columns (CC-49746).
const PL_COMPARE_NAME = ['None', 'PreviousPeriod', 'PreviousYear', 'YearToDate']

// CC-49594: publish the grid's export view to the surrounding shell so it can
// offer the client-built PDF (must render inside the shell's context provider).
//
// Spec 127 §4.2: every P&L grid — the statement AND the Account-Seg / Category
// matrix — renders in the grid's `statementMode`: sections never collapse, headers
// never sort (a sort persisted under report:pnl:* is ignored, not deleted), and the
// statement's parent accounts roll up one by one or all at once, showing CB's own
// rolled figures. The matrix carries no `tree`, so it gets no roll-up controls: it
// is already permanently top-level at CB's rolled totals, as in V1.
const PublishedGroupedGrid = ({ report, storageKey, pageStateRef, initialPageState }) => {
  const exportCtx = useReportExportView()
  const exportViewRef = useRef(null)
  useEffect(() => {
    if (!exportCtx) return
    exportCtx.ref.current = () => exportViewRef.current?.()
    exportCtx.onPublish?.()
  }, [exportCtx])
  return (
    <ReportGroupedGrid
      report={report}
      storageKey={storageKey}
      exportViewRef={exportViewRef}
      pageStateRef={pageStateRef}
      initialPageState={initialPageState}
      statementMode
    />
  )
}

// Spec 127 §8: why a statement offers no roll-up. Only a builder refusal reaches
// here (plStatement.js ROLLUP_UNAVAILABLE) — never observed on vladdemo, and
// said out loud rather than leaving the toggles silently missing. The words
// (ROLLUP_UNAVAILABLE_REASONS) live in plStatement.js since spec 143, which
// shares them with GL Budget vs Actual.

// CC-49904: the "Choose Report" modes V1's P&L page offers. 'Original' = the
// standard P&L; 'AccountSeg' pivots accounts across a segment's values
// (Location / Practice Area / Attorney); 'Category' groups by GL category.
const CHOOSE_REPORT = { ORIGINAL: 'Original', ACCOUNT_SEG: 'AccountSeg', CATEGORY: 'Category' }

// CC-49971 / CC-49973: V1 gates the Category P&L option on TWO conditions ANDed
// together — the `AccountingTrackCategories` tenant setting AND a non-empty
// category list (AccountingController.cs:2232-2251). cb-api resolves both in one
// call; see useGlCategories. cb2 previously read only the setting, which is why
// CC-50039 could reach a Category P&L with no category columns at all.


const makeDefaults = (isComparison) => ({
  startDate: dayjs().startOf('year').format('YYYY-MM-DD'),
  endDate: dayjs().format('YYYY-MM-DD'),
  isCashBasis: true,
  pLComparePeriods: isComparison ? 'PreviousYear' : 'None',
  dateRangeStr: 'This Year',
  // CC-49904: Choose Report mode + the segment it pivots on (Account Seg).
  chooseReport: CHOOSE_REPORT.ORIGINAL,
  accountSegValueId: 0,
  // CC-49973: GL category ids for Category P&L. Seeded when the user switches
  // into Category mode (see the Choose Report handler) so a category run is
  // never sent with an empty selection — CB reads an empty
  // `filteredCategories` as "no category filter" and answers with the ORDINARY
  // P&L, which is the misleading-output bug CC-50039 describes.
  categoryIds: [],
})

/**
 * The wire boundary — lifted verbatim from the page's old `params` memo, not
 * rewritten. Every line here is load-bearing:
 *
 *  - `pLComparePeriods` goes as an INTEGER via PL_COMPARE_WIRE. Sending the enum
 *    NAME makes CB answer 200 with all-zero comparison amounts (the blank /
 *    Change==Amount / em-dash fingerprint), and an unknown key must fall back to
 *    None (0) rather than to a comparison the user didn't ask for.
 *  - `accountSegValueId` is sent ONLY in Account-Seg mode; a non-zero id is what
 *    switches the server into that mode (CC-49904).
 *  - `filteredCategories` is sent ONLY in Category mode; a non-empty value is what
 *    switches the server into it, and an empty one silently yields the ORDINARY
 *    P&L (CC-50039). Pipe-delimited ints.
 *  - `dateRangeStr`, `isAdvanced`, `advancedRange`, `endAdjust`, `sortBy` and
 *    `sortDirection` have NO controls and must survive every filter edit.
 *  - Spec 127 §4.3 (1), CC-53903: for **Prior Period only**, `dateRangeStr`,
 *    `isAdvanced`, `advancedRange` and `endAdjust` come from `priorPeriodWire` —
 *    they are what CB's proc reads to pick the comparison window, and the old
 *    constants ('year' / 'This Year') made every Prior Period run compare with the
 *    12 months before the range. Every other mode (the plain P&L, Prior Year,
 *    Year to Date) keeps today's constants, so those wires — and their job dedupe
 *    keys — are byte-identical. A range `priorPeriodWire` refuses never reaches CB
 *    (the page blocks the run — `compareRefusal` below), so for that case the
 *    constants stand rather than a wire invented for a range CB cannot answer.
 */
const toWire = (f) => {
  const priorPeriod = f.pLComparePeriods === 'PreviousPeriod' ? priorPeriodWire(f.startDate, f.endDate) : null
  const win = priorPeriod && !priorPeriod.refuse ? priorPeriod : null
  return {
    startDate: f.startDate,
    endDate: f.endDate,
    isCashBasis: f.isCashBasis,
    pLComparePeriods: PL_COMPARE_WIRE[f.pLComparePeriods] ?? PL_COMPARE_WIRE.None,
    dateRangeStr: win ? win.dateRangeStr : f.dateRangeStr,
    isAdvanced: win ? win.isAdvanced : false,
    advancedRange: win ? win.advancedRange : 0,
    endAdjust: win ? win.endAdjust : 'year',
    sortBy: 'Account Name',
    sortDirection: 1,
    accountSegValueId: f.chooseReport === CHOOSE_REPORT.ACCOUNT_SEG ? (f.accountSegValueId || 0) : 0,
    filteredCategories: f.chooseReport === CHOOSE_REPORT.CATEGORY
      ? toFilteredCategories(f.categoryIds)
      : '',
  }
}

/**
 * Built per render: Comparison P&L exposes the Compare field and the ordinary P&L
 * does not.
 *
 * Only three of this page's controls are FILTERS in the metadata sense. Compare's
 * option values are CB enum member NAMES — `params`/`toWire` maps them to the wire
 * INTEGER via PL_COMPARE_WIRE, and sending the name instead makes CB return 200
 * with all-zero comparison amounts. `dateRangeStr` is a control-less constant.
 */
const buildFields = (isComparison) => [
  dateRangeField({ key: 'dates', label: 'Date Range', startKey: 'startDate', endKey: 'endDate', omitAll: true }),
  choiceField({
    key: 'basis', label: 'Basis', valueKey: 'isCashBasis',
    options: [{ value: 'cash', label: 'Cash' }, { value: 'accrual', label: 'Accrual' }],
    encode: (v) => (v ? 'cash' : 'accrual'),
    decode: (s) => s === 'cash',
  }),
  ...(isComparison
    ? [choiceField({
        key: 'compare', label: 'Compare', valueKey: 'pLComparePeriods',
        // Exactly the options V1 offers; there is no "Prior Month" on the server.
        options: [
          { value: 'PreviousYear', label: 'Prior Year' },
          { value: 'PreviousPeriod', label: 'Prior Period' },
          { value: 'YearToDate', label: 'Year to Date' },
          { value: 'None', label: 'None' },
        ],
      })]
    : []),
]

/**
 * CC-50029: the "Filters —" criteria line for this page's exports. A comparative
 * P&L and a plain one over the same window are different reports, and so are a
 * category-scoped one and a firm-wide one.
 *
 * Spec 127 §4.3 (3): `window` is the comparison window CB REPORTED for a run
 * ("Compared to Previous Period (01/01/2025 to 03/31/2025)"). It is a RESULT, not
 * a criterion, so it is only ever passed for a run that has landed — see
 * `runCriteria` in the page.
 */
const describePlCriteria = (f, categories, window) => describeFilters(
  dateRangePart(f.startDate, f.endDate),
  basisPart(f.isCashBasis),
  f.pLComparePeriods && f.pLComparePeriods !== 'None'
    // 'PreviousYear' -> 'Previous Year'
    ? `Compared to ${String(f.pLComparePeriods).replace(/([a-z])([A-Z])/g, '$1 $2')}${
      window ? ` (${describeComparisonWindow(window)})` : ''}`
    : null,
  // CC-49973: a category-scoped P&L and a firm-wide one over the same window
  // are different statements, so the export/PDF must say which it is — and
  // WHICH categories, because a partial selection excludes real money.
  f.chooseReport === CHOOSE_REPORT.CATEGORY && f.categoryIds.length
    ? `Categories: ${categoryNames(categories, f.categoryIds)}`
    : null,
)

// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err.message || 'Report failed'

const PLReport = ({ reportId, name = 'Profit & Loss', summary, isComparison = false, jobId }) => {
  const [filters, setFilters] = useState(() => makeDefaults(isComparison))
  // CC-49973: the tenant's GL categories + whether category reporting is offered
  // at all. Declared before filtersSummary because that stamp names the picked
  // categories.
  const glCategories = useGlCategories()
  // CC-50029: the reported defect — a P&L export carried its title and a
  // "Generated …" stamp but no date range, because this page never passed
  // `filtersSummary` to the shell. This is the LIVE panel line; the shell freezes
  // it when a run starts.
  const filtersSummary = useMemo(
    () => describePlCriteria(filters, glCategories.categories, null),
    [filters, glCategories.categories]
  )
  // Spec 127 §4.3 (3): the criteria line of the rows ON SCREEN, including the
  // comparison window CB reported for them. Set only when a fresh comparison run
  // LANDS with a readable window; null otherwise (the shell's own snapshot then
  // stands, byte-identical to before).
  //
  // Why the page, not the start-time criteria: those are captured when a run
  // STARTS (spec 131: `useLatestRun`'s `criteria`; before it, the shell's
  // runDocMetaRef) — correct for criteria, but the window is only known once CB
  // answers, so a start-time snapshot would label the new figures with the
  // PREVIOUS run's window. The shell's one page-authored override is
  // `savedRunFiltersSummary` ("wins over everything"); outside a reopen it is passed
  // only while this is set, i.e. only on a live comparison run (spec 185: on a reopen
  // it carries the saved job's own line, `savedCriteria`). A failed run keeps the previous
  // line, as the shell does, because the previous rows stay on screen. Spec 131:
  // written inside the run's COMMIT, so it can only ever describe the rows on
  // screen — a superseded comparison can no longer label the newer one's figures.
  const [runCriteria, setRunCriteria] = useState(null)
  // Spec 131: running / error / the committed run's criteria, gated so only the
  // NEWEST run may write any of them. Spinner immediately when reopening.
  const {
    running, error, ran, runKey, run: startRun, invalidate: invalidateRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  // CC-50013 (G3): saved views. The hook carries the grid's page state, so
  // the menu cannot be mounted without it (the CC-49732 defect).
  const view = useSavedReportView(setFilters)
  const [report, setReport] = useState(null)
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Spec 185 (CC-53435): the reopened job's own stored params — read ONLY to state the
  // saved run's criteria on the documents. Never seeded into `filters`: that would fire
  // useAutoRunOnFilterChange and replace the saved figures with a fresh run.
  const [savedParams, setSavedParams] = useState(null)
  // Consume-once: the first run reopens the tray's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)
  // Segment list (Location / Practice Area / Attorney) for the Account-Seg
  // picker — the server returns it alongside every P&L run.
  const [allSegments, setAllSegments] = useState([])
  // CC-49746: the compare period the DATA was run with — columns follow this,
  // not the live select, so changing Compare without re-running can't mislabel
  // the rendered figures.
  const [ranCompare, setRanCompare] = useState(isComparison ? 'PreviousYear' : 'None')
  // CC-49904: the mode the DATA was run with (columns/rendering follow it).
  const [ranMode, setRanMode] = useState(CHOOSE_REPORT.ORIGINAL)
  const showCategoryOption = glCategories.trackCategories

  // CC-49973: a Category run needs at least one category. An empty selection
  // would post `filteredCategories=''`, which CB answers with the ORDINARY P&L —
  // indistinguishable from a real category report in the output. Block the run
  // instead of rendering a firm-wide P&L under a Category heading (CC-50039).
  const categoryModeSelected = !isComparison && filters.chooseReport === CHOOSE_REPORT.CATEGORY
  const categorySelectionEmpty = categoryModeSelected && filters.categoryIds.length === 0

  const params = useMemo(() => toWire(filters), [filters])

  // Spec 127 §4.3 (2), CC-53903: refuse, don't guess, a comparison over an open or
  // inverted range. Measured: CB answers an open-ended comparison with an HTTP 500
  // or a window ending 9998-12-31. Blocked exactly like an empty Category selection
  // — Run disabled, a status note, no request. Keyed on the WIRE (a comparison is
  // being asked for), and only on the Comparative page: the plain P&L is unchanged.
  const compareRefusal = isComparison && params.pLComparePeriods !== PL_COMPARE_WIRE.None
    ? compareRangeRefusal(filters.startDate, filters.endDate)
    : null
  const runBlocked = categorySelectionEmpty || !!compareRefusal

  const runReport = useCallback(() => {
    // Guard BEFORE the fetch: an empty category selection, or a comparison over an
    // open/inverted range, must not reach CB. Spec 131: the refusal also RETIRES a
    // run still in flight, so it cannot land under the refusal note. No error: this
    // refusal speaks through its own status note, never the red pane.
    if (runBlocked) { invalidateRun(); return undefined }
    // Spec 131: everything below the awaits returns a COMMIT; useLatestRun runs it
    // only if this is still the newest run, so a superseded result is dropped.
    return startRun(async () => {
      const jid = jobRef.current
      jobRef.current = null // consumed — subsequent runs are fresh
      if (jid) {
        // Tray "View report" → reopen the saved snapshot. Restore the run-STATE that
        // drives column rendering (compare period + account-seg mode) from the saved
        // job's params so the columns match — WITHOUT touching filters (which would
        // re-trigger the auto-run and clobber the saved data).
        const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
        const sp = st?.savedParams || {}
        const data = r?.data
        return () => {
          setSavedAsOf(st?.createdAt || null)
          setReport(data?.report || null)
          // A reopen's criteria are the shell's to state (CC-53363): the page's
          // filters are its DEFAULTS here, so it must not describe the saved run.
          // Spec 185: the JOB's own params describe it instead (`savedCriteria`).
          setRunCriteria(null)
          setSavedParams(st?.savedParams ?? null)
          if (Array.isArray(data?.allSegments)) setAllSegments(data.allSegments)
          setRanCompare(PL_COMPARE_NAME[Number(sp.pLComparePeriods) || 0] || 'None')
          // CC-49973: a reopened job must restore the mode its DATA was run in, or
          // a saved Category P&L renders through the standard two-column grid and
          // silently drops every category column.
          setRanMode(
            String(sp.filteredCategories || '') !== '' ? CHOOSE_REPORT.CATEGORY
              : Number(sp.accountSegValueId) > 0 ? CHOOSE_REPORT.ACCOUNT_SEG
                : CHOOSE_REPORT.ORIGINAL
          )
        }
      }
      const data = await accountingReportService.pnl.run(params, { reportId })
      // `filters` is this run's own closure — the filters the fetch was issued
      // with — so the line describes exactly this request plus CB's answer.
      const ranWindow = isComparison && filters.pLComparePeriods !== 'None'
        ? comparisonWindowOf(data?.report)
        : null
      return () => {
        // Spec 131 decision 3: the saved-result banner clears when a fresh run
        // COMMITS, not when it starts — the saved rows stay on screen until then.
        setSavedAsOf(null)
        setReport(data?.report || null)
        setRunCriteria(ranWindow ? describePlCriteria(filters, glCategories.categories, ranWindow) : null)
        if (Array.isArray(data?.allSegments)) setAllSegments(data.allSegments)
        // Track the enum NAME, not the wire integer: comparisonActive and the
        // column labels both key off the name.
        setRanCompare(filters.pLComparePeriods)
        setRanMode(params.accountSegValueId > 0 ? CHOOSE_REPORT.ACCOUNT_SEG : filters.chooseReport)
      }
    }, { criteria: { filtersSummary } })
  }, [params, filters, isComparison, glCategories.categories, reportId, runBlocked, startRun, invalidateRun, filtersSummary])

  // CC-49835: first load + re-run whenever a filter changes (debounced), same
  // behavior as the rest of the financial-statement family. The Run button
  // stays for an explicit re-run.
  useAutoRunOnFilterChange(runReport, { ...params, chooseReport: filters.chooseReport })

  const sections = useMemo(() => report?.sections || [], [report])
  // Spec 127 §4.3 (6): `??`, never `||` — a Net Income of exactly 0 is a Net
  // Income (measured: "Today"/"Yesterday" return netIncome 0 on vladdemo).
  const netIncome = netIncomeOf(report)
  // CC-49746: comparison columns render when the data was run with a compare period.
  const comparisonActive = isComparison && ranCompare !== 'None'
  // Spec 127 §4.3 (3): on a comparison run `report.startDate`/`endDate` carry the
  // window CB actually compared with. Shown above the grid (and, via
  // `runCriteria`, in the export criteria line), so the comparison is never
  // unlabelled again. A reopened job reads the same two fields from its stored
  // payload.
  const compareWindow = useMemo(
    () => (comparisonActive ? comparisonWindowOf(report) : null),
    [comparisonActive, report]
  )
  // Spec 185 (CC-53435): a reopened job's criteria line, from ITS stored params and
  // stored report — the builder the queue drawer prints with (savedRunCriteria.js), so
  // "Download PDF" in the drawer and "Open report" → Export say the same thing. Null
  // (the shell's "not recorded") when the job's params cannot state the dates and basis,
  // for a Category P&L, and for a comparison job on the plain P&L page.
  const savedCriteria = useMemo(
    () => (savedAsOf != null ? pnlSavedCriteria(savedParams, report, { comparisonPage: isComparison }) : null),
    [savedAsOf, savedParams, report, isComparison]
  )
  // CC-49904: the report was RUN in Account-Seg mode (columns follow the data).
  const accountSegActive = ranMode === CHOOSE_REPORT.ACCOUNT_SEG
  // CC-49973: …or in Category mode. Both render the same pivot matrix.
  const categoryActive = ranMode === CHOOSE_REPORT.CATEGORY
  // Hoisted above `filterBar` (CC-50013): the saved-views menu inside the filter
  // bar keys on the same storageKey as the grid, and `filterBar` is a const
  // whose JSX is EVALUATED where it is declared — reading matrixActive from its
  // old position further down the component was a temporal-dead-zone crash, not
  // a stale value. Declared here beside the two flags it derives from.
  const matrixActive = (accountSegActive || categoryActive) && hasPivotColumns(report)

  // Account-Seg (CC-49904) and Category (CC-49973) P&L both pivot each account
  // across `accountSegmentInfo`. One builder serves both so they cannot drift;
  // see segmentMatrix.js for the payload facts (all figures pass through from the
  // server — nothing is recomputed here).
  const matrixGridReport = useMemo(
    () => buildSegmentMatrix({
      report,
      sections,
      netIncome,
      mode: categoryActive ? 'category' : 'segment',
    }),
    [report, sections, netIncome, categoryActive]
  )

  // Spec 127 S2: the statement is built by ONE pure function (plStatement.js),
  // moved out of this memo with the Comparative wrong-number fixes — the plain
  // Net Income change, no fabricated 0.0%, `dataValue` + $0.00 on every total.
  // S4: it also carries the roll-up tree, the Account Number column and CB's Gross
  // Profit / Net Operating Income lines; indentation is the grid's, from the tree.
  const gridReport = useMemo(
    () => buildPlStatementReport(report, { comparisonActive, ranCompare }),
    [report, comparisonActive, ranCompare]
  )

  // CC-50013 G2 / CC-50148: shared metadata-driven bar. Built per render because
  // the Compare field exists only on Comparison P&L.
  //
  // `params` above stays the wire boundary and is deliberately untouched — it
  // carries two landmines: the PL_COMPARE_WIRE name→INTEGER mapping (sending the
  // enum NAME makes CB answer 200 with all-zero comparison amounts), and the
  // mode-conditional `accountSegValueId` / `filteredCategories`.
  //
  // Everything below Choose Report stays a PAGE control in the action row, not a
  // filter field, because each changes the report's SHAPE rather than its scope
  // and they interlock: entering Category mode preselects every category
  // (CC-49973), an empty category selection is blocked outright (CC-50039), and
  // Account-Seg mode reveals a segment picker whose options come from the last
  // run. Modelling those as independent filters would imply they combine freely.
  const fields = useMemo(() => buildFields(isComparison), [isComparison])
  const filterBar = (
    <SpecializedFilterBar
      fields={fields}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults(isComparison)}
      onRun={runReport}
      isLoading={running}
      disabled={runBlocked}
      queue={{
        reportId,
        name,
        // Mirrors accountingReportService.pnl.run: an ACCOUNTING job (GET replay),
        // key 'pnl' — the one accounting report that IS firm-shareable
        // server-side, so its dedupe can be reused across users.
        kind: 'accounting',
        accountingKey: 'pnl',
        endpoint: (f) => accountingEndpoint('/reports/accounting/pnl', toWire(f)),
        filters: (f) => toWire(f),
        // Spec 194: the emailed copy can carry this page's own export.
        attachFormats: accountingAttachFormats('/reports/accounting/pnl'),
      }}
      actions={(
        <>
          {/* CC-49904: Choose Report — the mode selector V1 shows on P&L.
              Comparison P&L doesn't offer the segment/category pivots. */}
          {!isComparison && (
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
              Choose Report
              {/* 2026-08-26 ADA sweep. Both selects on this bar wore
                  `text-sm font-normal normal-case border border-gray-200
                  rounded-lg px-2.5 py-1.5`; #e5e7eb is 1.24:1 and a
                  white-on-white select's 1px border is the only thing marking it
                  as a control (SC 1.4.11 → 3.0:1; --color-rc-control is 3.03:1).
                  `.select-rc` brings the border, the 6px radius, the 14px label
                  and the shared focus outline, so the geometry utilities are
                  DELETED — the class is unlayered and would have made every one
                  of them dead code.

                  `normal-case` is the ONE that stays, and it is not decoration:
                  the wrapping <label> is `uppercase`, text-transform inherits,
                  and `.select-rc` does not set it. Drop `normal-case` and every
                  option renders shouting. */}
              <select
                value={filters.chooseReport} aria-label="Choose report"
                // CC-49973: entering Category mode preselects EVERY category
                // (Uncategorized included) so the first run is a genuine, complete
                // category statement. Leaving the selection empty would post
                // filteredCategories='' and CB would answer with the ordinary P&L.
                onChange={(e) => {
                  const next = e.target.value
                  setFilters((f) => ({
                    ...f,
                    chooseReport: next,
                    categoryIds: next === CHOOSE_REPORT.CATEGORY && f.categoryIds.length === 0
                      ? allCategoryIds(glCategories.categories)
                      : f.categoryIds,
                  }))
                }}
                className="select-rc normal-case"
              >
                <option value={CHOOSE_REPORT.ORIGINAL}>P&amp;L</option>
                <option value={CHOOSE_REPORT.ACCOUNT_SEG}>Account Seg P&amp;L</option>
                {/* Offered only when this tenant tracks categories AND has some —
                    V1's two-condition gate. Never offered while the list is still
                    resolving (useGlCategories starts false), because a Category run
                    with no categories is a firm-wide P&L wearing the wrong label. */}
                {showCategoryOption && <option value={CHOOSE_REPORT.CATEGORY}>Category P&amp;L</option>}
              </select>
            </label>
          )}
          {/* Category mode: which GL categories to break the statement out by. */}
          {categoryModeSelected && (
            <CategorySelect
              categories={glCategories.categories}
              value={filters.categoryIds}
              onChange={(ids) => setFilters((f) => ({ ...f, categoryIds: ids }))}
            />
          )}
          {/* Account-Seg mode: pick which segment to pivot on (V1's Group By). */}
          {!isComparison && filters.chooseReport === CHOOSE_REPORT.ACCOUNT_SEG && (
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
              Group By
              <select
                value={String(filters.accountSegValueId || 0)} aria-label="Account segment"
                onChange={(e) => setFilters((f) => ({ ...f, accountSegValueId: parseInt(e.target.value, 10) }))}
                className="select-rc normal-case"
              >
                <option value="0">Select…</option>
                {allSegments.map((s) => (
                  <option key={s.segmentId} value={String(s.segmentId)}>{s.label}</option>
                ))}
              </select>
            </label>
          )}
          <ReportViewsMenu
            storageKey={`report:pnl:${matrixActive ? (categoryActive ? 'cat' : 'seg') : 'std'}`}
            currentFilters={filters}
            onApply={view.applyView}
            getPageState={view.getPageState}
          />
        </>
      )}
    />
  )

  {/* CC-50039: an empty selection cannot silently become a firm-wide P&L. The
      warning sits OUTSIDE the filter bar now, directly above the grid, so it stays
      visible with the bar's own layout unchanged. */}
  const categoryWarning = categorySelectionEmpty ? (
    <div
      role="status"
      className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 mb-4 text-sm text-amber-900"
    >
      <span aria-hidden="true" className="mt-px font-semibold">ⓘ</span>
      <span>
        <strong className="font-semibold">Select at least one category.</strong>{' '}
        A Category Profit &amp; Loss needs at least one GL category — with none selected
        this would run as an ordinary firm-wide P&amp;L.
      </span>
    </div>
  ) : null

  // Spec 127 §4.3 (2): the refused comparison says why, in the same place and
  // shape as the category warning. `role="status"` so the reason is announced.
  const compareRangeNote = compareRefusal ? (
    <div
      role="status"
      data-testid="pl-compare-range-refused"
      className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 mb-4 text-sm text-amber-900"
    >
      <span aria-hidden="true" className="mt-px font-semibold">ⓘ</span>
      <span>{COMPARE_RANGE_MESSAGES[compareRefusal]}</span>
    </div>
  ) : null

  // Spec 127 §4.2 / §8: the statement builder refused a tree (an account id
  // repeated, or missing) — say so, in the same place and shape as the notes
  // above, instead of leaving the roll-up controls silently absent. Never on the
  // matrix, which has no roll-up to lose.
  const rollupUnavailable = matrixActive ? null : gridReport.rollupUnavailable
  const rollupNote = rollupUnavailable ? (
    <div
      role="status"
      data-testid="pl-rollup-unavailable"
      className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 mb-4 text-sm text-amber-900"
    >
      <span aria-hidden="true" className="mt-px font-semibold">ⓘ</span>
      <span>{`Roll-up is unavailable for this run: ${ROLLUP_UNAVAILABLE_REASONS[rollupUnavailable]}`}</span>
    </div>
  ) : null

  // CC-49904 / CC-49973: Account-Seg and Category modes swap the pivot matrix in
  // once the data has actually been run in that mode; otherwise the standard P&L
  // grid. `accountSegmentInfo` must carry a real (non-sentinel) column — with
  // only the -1 "All others" sentinel there is nothing to pivot on, so falling
  // back to the standard grid shows the statement rather than an empty matrix.
  const activeGrid = matrixActive ? matrixGridReport : gridReport

  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar}
      filtersSummary={filtersSummary} savedAsOf={savedAsOf}
      /* A reopen: the saved job's own line (or null → "not recorded"). A live
         comparison run: its line with CB's reported window. Otherwise the shell's
         start-time snapshot stands. */
      savedRunFiltersSummary={savedAsOf != null ? savedCriteria : (runCriteria || undefined)}
      download={(format, getToken) => accountingReportService.pnl.download(params, format, getToken)}
    >
      {/* CC-49973: a category run that matched nothing would otherwise render an
          empty statement, which on a P&L reads as "the firm earned nothing".
          Say why instead. */}
      {categoryActive && !matrixActive && !running && report && (
        <div
          role="status"
          className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <span aria-hidden="true" className="mt-px font-semibold">ⓘ</span>
          <span>
            <strong className="font-semibold">No activity for the selected categories.</strong>{' '}
            Nothing in this date range is assigned to them, so this is not a category
            breakdown — {sections.length > 0
              ? 'the figures below are the standard Profit & Loss'
              : 'and CB returned no figures at all'}.
            Try a wider date range, or include <em>Uncategorized</em>.
          </span>
        </div>
      )}
      {categoryWarning}
      {compareRangeNote}
      {rollupNote}
      {/* Spec 127 §4.3 (3): the window CB compared with, straight from the
          payload. cb2's header used to say only "Prior Period" — which, until this
          spec, meant a 12-month window whatever the range. #6b7280 on white 4.83:1. */}
      {compareWindow && !matrixActive && (
        <p data-testid="pl-compare-window" className="mb-2 text-sm text-rc-muted">
          {`Compared with ${describeComparisonWindow(compareWindow)}`}
        </p>
      )}
      <PublishedGroupedGrid key={view.gridKey}
        report={activeGrid}
        storageKey={`report:pnl:${matrixActive ? (categoryActive ? 'cat' : 'seg') : 'std'}`} pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} />
    </SpecializedReportShell>
  )
}

export const PLComparisonReport = (props) => <PLReport {...props} isComparison name={props.name || 'Profit & Loss Comparison'} />

export default PLReport
